/**
 * CalendarEdit — /calendars/new
 *
 * Minimal create form. After save, redirects to /calendars/:slug.
 */
import React, { useState } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useSEO } from '../useSEO';
import { calendarsApi } from './api';

const EMOJI_CHOICES = ['📅', '📚', '🧪', '🧮', '✏️', '📝', '🎯', '🏆', '🧠', '🔬', '📖', '⏰'];

export default function CalendarEdit() {
  useSEO({
    title: 'New revision calendar',
    description: 'Create a shareable revision calendar to plan study sessions and attach past papers, mark schemes and notes.',
    canonicalPath: '/calendars/new',
  });

  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('unlisted');
  const [coverEmoji, setCoverEmoji] = useState('📅');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!user) return <Navigate to="/login" replace state={{ from: '/calendars/new' }} />;

  async function onSubmit(e) {
    e.preventDefault();
    if (!title.trim()) { setError('Please give your calendar a title.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await calendarsApi.create({
        title: title.trim(),
        description: description.trim(),
        visibility,
        coverEmoji,
      }, token);
      navigate(`/calendars/${res.calendar.shareSlug}`);
    } catch (err) {
      setError(err.message || 'Failed to create calendar');
      setSaving(false);
    }
  }

  return (
    <div className="tt-page cal-edit-page">
      <header className="tt-header">
        <div>
          <h1>New calendar</h1>
          <p className="tt-sub">Set a title and visibility. You can add days and resources next.</p>
        </div>
        <div className="tt-header-actions">
          <Link to="/calendars" className="secondary-btn">Cancel</Link>
        </div>
      </header>

      <form className="cal-edit-form" onSubmit={onSubmit}>
        {error && <div className="tt-status error">{error}</div>}

        <label className="tt-field">
          <span>Calendar title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. May Half-Term Revision Plan"
            maxLength={200}
            required
          />
        </label>

        <label className="tt-field">
          <span>Description (optional)</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="A short description of what this plan covers and who it’s for."
            rows={4}
            maxLength={2000}
          />
        </label>

        <fieldset className="cal-edit-fieldset">
          <legend>Cover icon</legend>
          <div className="cal-emoji-grid">
            {EMOJI_CHOICES.map((e) => (
              <button
                type="button"
                key={e}
                className={`cal-emoji-btn${coverEmoji === e ? ' active' : ''}`}
                onClick={() => setCoverEmoji(e)}
                aria-pressed={coverEmoji === e}
              >{e}</button>
            ))}
          </div>
        </fieldset>

        <fieldset className="cal-edit-fieldset">
          <legend>Visibility</legend>
          <label className="cal-radio">
            <input
              type="radio"
              name="visibility"
              value="unlisted"
              checked={visibility === 'unlisted'}
              onChange={() => setVisibility('unlisted')}
            />
            <span>
              <strong>Link only</strong>
              <small>Anyone with the share link can view; not listed publicly.</small>
            </span>
          </label>
          <label className="cal-radio">
            <input
              type="radio"
              name="visibility"
              value="public"
              checked={visibility === 'public'}
              onChange={() => setVisibility('public')}
            />
            <span>
              <strong>Public</strong>
              <small>Discoverable from the calendars page and Home.</small>
            </span>
          </label>
        </fieldset>

        <div className="cal-edit-actions">
          <button type="submit" className="primary-btn" disabled={saving}>
            {saving ? 'Creating\u2026' : 'Create calendar'}
          </button>
        </div>
      </form>
    </div>
  );
}
